import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { getMyAccount } from "@/lib/account.functions";
import { COMPANY_NAME, COMPANY_URL, SUPPORT_EMAIL } from "@/lib/brand";
import { LegalFooter } from "@/components/LegalFooter";
import { Mail, RotateCcw } from "lucide-react";
import { buildEmailConfirmationRedirect } from "@/lib/signup-resume";

export const Route = createFileRoute("/application-status")({
  head: () => ({
    meta: [
      { title: `Application status — ${COMPANY_NAME}` },
      { name: "description", content: `Track the status of your application to ${COMPANY_NAME}. See your next steps to complete onboarding.` },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: `Application status — ${COMPANY_NAME}` },
      { property: "og:url", content: `${COMPANY_URL}/application-status` },
    ],
    links: [{ rel: "canonical", href: `${COMPANY_URL}/application-status` }],
  }),
  component: ApplicationStatusPage,
});

type StepState = "done" | "current" | "pending" | "blocked";

interface Step {
  key: string;
  title: string;
  description: string;
  state: StepState;
  action?: { label: string; to: string };
  extra?: React.ReactNode;
}

interface AccountSummary {
  email: string | null;
  emailConfirmed: boolean;
  role: string | null;
  onboardingStatus: string | null;
  rtwStatus: string | null;
  isVerified: boolean;
  insuranceStatus: string | null;
}

function ApplicationStatusPage() {
  const navigate = useNavigate();
  const fetchAccount = useServerFn(getMyAccount);
  const [loading, setLoading] = useState(true);
  const [signedIn, setSignedIn] = useState(false);
  const [account, setAccount] = useState<AccountSummary | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendStatus, setResendStatus] = useState<"idle" | "success" | "error">("idle");
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<number>(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [cooldownUntil]);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data: userRes } = await supabase.auth.getUser();
      if (!active) return;
      if (!userRes.user) {
        setSignedIn(false);
        setLoading(false);
        return;
      }
      setSignedIn(true);
      try {
        const res = await fetchAccount();
        if (!active) return;
        const p = res.profile as Record<string, unknown> | null;
        setAccount({
          email: res.email,
          emailConfirmed: res.emailConfirmed,
          role: (p?.role as string) ?? null,
          onboardingStatus: (p?.onboarding_status as string) ?? null,
          rtwStatus: (p?.rtw_status as string) ?? null,
          isVerified: Boolean(p?.is_verified),
          insuranceStatus: (p?.insurance_status as string) ?? null,
        });
      } catch (err) {
        console.warn("[application-status] fetch failed", err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [fetchAccount]);

  const cooldownRemaining = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));

  const handleResendConfirmation = async () => {
    if (!account?.email || resendLoading || cooldownRemaining > 0) return;
    setResendLoading(true);
    setResendStatus("idle");
    setResendMessage(null);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: account.email,
        options: {
          emailRedirectTo: buildEmailConfirmationRedirect(window.location.origin, "/dashboard"),
        },
      });
      if (error) {
        setResendStatus("error");
        // Friendlier copy for the most common failure — rate limiting.
        const raw = error.message || "";
        setResendMessage(
          /rate|too many|429/i.test(raw)
            ? "Too many requests — please wait a minute before trying again."
            : raw || "Something went wrong. Please try again in a moment.",
        );
      } else {
        setResendStatus("success");
        setResendMessage("Confirmation email resent — check your inbox and spam folder.");
        setCooldownUntil(Date.now() + 60_000);
        setNow(Date.now());
      }
    } catch (err) {
      setResendStatus("error");
      setResendMessage(err instanceof Error ? err.message : "Network error — please try again.");
    } finally {
      setResendLoading(false);
    }
  };


  const steps: Step[] = buildSteps(account, handleResendConfirmation, resendLoading, resendStatus, resendMessage, cooldownRemaining);
  const submittedLabel = account?.onboardingStatus
    ? statusLabel(account.onboardingStatus)
    : signedIn
      ? "Awaiting your details"
      : "Awaiting email confirmation";

  return (
    <div className="min-h-dvh bg-background text-foreground flex flex-col">
      <header className="flex items-center justify-between gap-4 px-4 py-5 sm:px-6 lg:px-16">
        <Link to="/" className="inline-block min-w-0">
          <p className="truncate tracking-wide-luxury text-accent text-xs">The Hospitality</p>
          <p className="truncate font-display text-xl">Reserve</p>
        </Link>
        {signedIn ? (
          <button
            type="button"
            onClick={async () => {
              await supabase.auth.signOut();
              navigate({ to: "/", replace: true });
            }}
            className="shrink-0 text-xs tracking-wide-luxury text-muted-foreground hover:text-accent transition-colors"
          >
            Sign out
          </button>
        ) : (
          <Link
            to="/auth"
            className="shrink-0 border border-accent px-4 py-2 text-xs tracking-wide-luxury text-accent hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            Sign in
          </Link>
        )}
      </header>

      <main className="flex-1 px-4 py-10 sm:px-6 lg:px-16">
        <div className="mx-auto max-w-2xl">
          <p className="tracking-wide-luxury text-accent">Application status</p>
          <span className="gold-divider mt-4" />
          <h1 className="mt-5 font-display text-3xl sm:text-4xl">Thank you for applying.</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            We review every application personally. Here's where you are and what's needed next
            to get you onto the marketplace.
          </p>

          <div className="mt-8 border border-border bg-white p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[10px] tracking-wide-luxury text-accent">Current state</p>
                <p className="mt-1 font-display text-xl text-foreground">{submittedLabel}</p>
              </div>
              {account?.role && (
                <span className="border border-accent/40 bg-accent/5 px-3 py-1 text-[10px] tracking-wide-luxury text-accent capitalize">
                  {account.role}
                </span>
              )}
            </div>
            {account?.email && (
              <p className="mt-4 text-xs text-muted-foreground break-all">
                Signed in as <span className="text-foreground">{account.email}</span>
              </p>
            )}
          </div>

          {loading ? (
            <p aria-live="polite" className="mt-8 text-sm text-muted-foreground">Loading your progress…</p>
          ) : (
            <ol className="mt-8 space-y-3">
              {steps.map((step, i) => (
                <li
                  key={step.key}
                  aria-current={step.state === "current" ? "step" : undefined}
                  className={`border p-5 ${
                    step.state === "current"
                      ? "border-accent bg-accent/5"
                      : step.state === "done"
                        ? "border-border bg-white"
                        : step.state === "blocked"
                          ? "border-destructive/40 bg-destructive/5"
                          : "border-border bg-white/60"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <StepBadge state={step.state} index={i + 1} />
                    <div className="flex-1 min-w-0">
                      <h2 className="font-display text-lg text-foreground">
                        {step.title}
                        <span className="sr-only">
                          {step.state === "done"
                            ? " — complete"
                            : step.state === "current"
                              ? " — in progress"
                              : step.state === "blocked"
                                ? " — action needed"
                                : " — not started"}
                        </span>
                      </h2>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                        {step.description}
                      </p>
                      {step.action && step.state !== "done" && (
                        <Link
                          to={step.action.to}
                          className="mt-4 inline-block border border-accent px-4 py-2 text-[10px] tracking-wide-luxury text-accent hover:bg-accent hover:text-accent-foreground transition-colors"
                        >
                          {step.action.label}
                        </Link>
                      )}
                      {step.extra}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}

          <div className="mt-10 border-t border-border pt-6 text-xs text-muted-foreground">
            <p>
              Questions or something not quite right? Email us at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-accent hover:underline">
                {SUPPORT_EMAIL}
              </a>{" "}
              — we'll always reply within one working day.
            </p>
          </div>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}

function StepBadge({ state, index }: { state: StepState; index: number }) {
  const base =
    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-medium border";
  if (state === "done")
    return <span aria-hidden="true" className={`${base} border-accent bg-accent text-accent-foreground`}>✓</span>;
  if (state === "current")
    return <span aria-hidden="true" className={`${base} border-accent text-accent`}>{index}</span>;
  if (state === "blocked")
    return <span aria-hidden="true" className={`${base} border-destructive text-destructive`}>!</span>;
  return <span aria-hidden="true" className={`${base} border-border text-muted-foreground`}>{index}</span>;
}

function statusLabel(status: string): string {
  switch (status) {
    case "approved":
      return "Approved — welcome to the Reserve";
    case "pending":
      return "Under review";
    case "submitted":
      return "Submitted — under review";
    case "rejected":
      return "Not approved — see details below";
    case "incomplete":
      return "Awaiting your details";
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

function buildSteps(
  a: AccountSummary | null,
  onResend?: () => void,
  resendLoading?: boolean,
  resendStatus?: "idle" | "success" | "error",
  resendMessage?: string | null,
  cooldownRemaining?: number,
): Step[] {
  // Not signed in / no account fetched yet — public "post-application" view.
  if (!a) {
    return [
      {
        key: "email",
        title: "Confirm your email",
        description:
          "We've just sent you a confirmation link. Click it to activate your account, then return here to continue. Didn’t receive it? Check spam/junk, add our address to your contacts, or sign in to resend it.",
        state: "current",
        action: { label: "I've confirmed — sign in", to: "/auth" },
      },
      {
        key: "details",
        title: "Complete your profile",
        description:
          "Tell us a little about your experience (bartenders) or your venue (venues). Takes about 3 minutes.",
        state: "pending",
      },
      {
        key: "review",
        title: "Our review",
        description:
          "Our team personally reviews every application. Most are cleared within one working day.",
        state: "pending",
      },
      {
        key: "live",
        title: "Go live on the marketplace",
        description:
          "Bartenders start receiving shift alerts within your travel radius. Venues can post their first shift immediately.",
        state: "pending",
      },
    ];
  }

  const isBartender = a.role === "bartender";
  const emailDone = a.emailConfirmed;
  const detailsDone = a.onboardingStatus === "submitted" || a.onboardingStatus === "pending" || a.onboardingStatus === "approved";
  const reviewDone = a.onboardingStatus === "approved";
  const rejected = a.onboardingStatus === "rejected";

  const detailsAction = isBartender
    ? { label: "Complete bartender profile", to: "/bartender/onboarding" }
    : { label: "Complete venue profile", to: "/venue/onboarding" };

  const cooldown = cooldownRemaining ?? 0;
  const buttonLabel = resendLoading
    ? "Sending…"
    : cooldown > 0
      ? `Resend available in ${cooldown}s`
      : "Resend confirmation email";

  const emailExtra = !emailDone ? (
    <div className="mt-4">
      <button
        type="button"
        onClick={onResend}
        disabled={resendLoading || cooldown > 0}
        aria-busy={resendLoading}
        className="inline-flex items-center gap-2 border border-accent px-4 py-2 text-[10px] tracking-wide-luxury text-accent hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <RotateCcw className={`h-3 w-3 ${resendLoading ? "animate-spin" : ""}`} />
        {buttonLabel}
      </button>
      {resendMessage && resendStatus === "success" && (
        <p role="status" aria-live="polite" className="mt-3 flex items-start gap-1.5 text-xs text-accent">
          <Mail className="mt-0.5 h-3 w-3 shrink-0" />
          {resendMessage}
        </p>
      )}
      {resendMessage && resendStatus === "error" && (
        <p role="alert" aria-live="assertive" className="mt-3 flex items-start gap-1.5 text-xs text-destructive">
          <Mail className="mt-0.5 h-3 w-3 shrink-0" />
          {resendMessage}
        </p>
      )}
      <div className="mt-4 border-l-2 border-accent/30 pl-3 text-xs leading-relaxed text-muted-foreground space-y-2">
        <p className="text-foreground/80">Email delayed or missing? Try these in order:</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>Check your spam, junk, or promotions folder.</li>
          <li>
            Add{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-accent hover:underline">
              {SUPPORT_EMAIL}
            </a>{" "}
            to your contacts so future emails land in your inbox.
          </li>
          <li>Corporate or school inboxes sometimes block external sign-ups — try a personal Gmail/Outlook address instead.</li>
          <li>
            Still nothing after a few minutes? Email us at{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-accent hover:underline">
              {SUPPORT_EMAIL}
            </a>{" "}
            and we’ll verify you manually within one working day.
          </li>
        </ol>
      </div>
    </div>
  ) : undefined;

  const steps: Step[] = [
    {
      key: "email",
      title: "Confirm your email",
      description: emailDone
        ? "Your email address is verified."
        : `We sent a confirmation link to ${a.email ?? "your inbox"}. Click it to verify your address and unlock the rest of your application.`,
      state: emailDone ? "done" : "current",
      extra: emailExtra,
    },
    {
      key: "details",
      title: isBartender ? "Complete your bartender profile" : "Complete your venue profile",
      description: detailsDone
        ? "Your details are in — thank you."
        : isBartender
          ? "Tell us about your experience, share a couple of references, and confirm your right to work and insurance."
          : "Confirm your trading details, address, and a point of contact so bartenders know who they're working with.",
      state: !emailDone ? "pending" : detailsDone ? "done" : "current",
      action: detailsDone ? undefined : detailsAction,
    },
  ];

  if (isBartender) {
    const rtwDone = a.rtwStatus === "verified";
    steps.push({
      key: "rtw",
      title: "Right to Work check",
      description: rtwDone
        ? "Your Right to Work is verified."
        : "A quick digital check via Veriff. Usually completes in a few minutes.",
      state: !detailsDone ? "pending" : rtwDone ? "done" : "current",
      action: rtwDone ? undefined : { label: "Start Veriff check", to: "/account" },
    });
  }

  steps.push({
    key: "review",
    title: "Our review",
    description: reviewDone
      ? "You're approved. Welcome aboard."
      : rejected
        ? "We weren't able to approve your application at this time. Please contact us if you'd like more information."
        : "Once your details are in, a human reviews your application — usually within one working day.",
    state: rejected ? "blocked" : reviewDone ? "done" : detailsDone ? "current" : "pending",
  });

  steps.push({
    key: "live",
    title: "Go live on the marketplace",
    description: reviewDone
      ? isBartender
        ? "Shift alerts will start arriving within your travel radius. You can also browse the register directly."
        : "You can post your first shift now — bartenders in the area will be notified instantly."
      : "Unlocked once your application is approved.",
    state: reviewDone ? "current" : "pending",
    action: reviewDone ? { label: "Open dashboard", to: "/dashboard" } : undefined,
  });

  return steps;
}
